import React, { useContext, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ConsultationScheduler from '../NewConsultationForm/ConsultationScheduler';
import { FieldErrors } from '../../../hooks/useFormState.types';
import {
  EnergyConsultationDetails,
  EnergyConsultationMonthAvailabilityResponse,
  MutationRescheduleEnergyConsultationArgs,
  QueryGetEnergyConsultationMonthAvailabilityArgs,
  RescheduleEnergyConsultationResponse,
} from '../../../__generated__/pge-types';
import useFormState from '../../../hooks/useFormState';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatDate } from '../../../util/format';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import { useMutation } from '@apollo/react-hooks';
import {
  getAvailableDatesForConsultation,
  rescheduleEnergyConsultationMutation,
} from '../queries';
import mainColors from '../../../themes/main-colors';
import useAuthQuery from '../../../hooks/useAuthQuery';
import format from 'date-fns/format';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      width: 560,
      maxWidth: '95%',
      borderRadius: 12,
    },
    header: {
      backgroundColor: mainColors.modalBlue,
      padding: theme.spacing(2, 3),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: 18,
      fontWeight: 700,
    },
    closeButton: {
      backgroundColor: mainColors.gray97,
      padding: 6,
    },
    content: {
      padding: theme.spacing(3),
    },
    fieldLabel: {
      fontSize: 14,
      fontWeight: 500,
      marginBottom: theme.spacing(0),
    },
    fieldValue: {
      fontSize: 16,
      marginBottom: theme.spacing(2),
    },
    footer: {
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${mainColors.disableColor}`,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '16px',
    },
    cancelButton: {
      minWidth: 120,
      color: mainColors.sparkBlue,
      borderColor: mainColors.sparkBlue,
      textTransform: 'none',
      fontWeight: 600,
    },
    submitButton: {
      minWidth: 120,
      backgroundColor: mainColors.orange,
      color: mainColors.white,
      textTransform: 'none',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: mainColors.orange,
      },
    },
  }),
);
interface RescheduleEnergyConsultationForm {
  newPreferredDate: Date | undefined;
  newPreferredTimeSlot: string;
}

type Context = {
  t: (s: string) => string;
};

function validate(data: RescheduleEnergyConsultationForm, context: Context) {
  const errors: FieldErrors<RescheduleEnergyConsultationForm> = {};
  if (!data.newPreferredDate) {
    errors.newPreferredDate = `${context.t('ENTER_THE')} ${context.t('DATE')}`;
  }

  if (!data.newPreferredTimeSlot) {
    errors.newPreferredTimeSlot = `${context.t('ENTER_THE')} ${context.t(
      'TIME',
    )}`;
  }
  return errors;
}

export interface RescheduleConsultationModalProps {
  request: EnergyConsultationDetails;
  open: boolean;
  onClose: () => void;
}

const RescheduleConsultationModal: React.FC<RescheduleConsultationModalProps> = ({
  request,
  open,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { wrapWithLoader } = useWrapWithLoader();
  const notificationContext = useContext(NotificationsContext);

  const requestId = request?.requestId;
  const currentDate = request?.requestDetails?.preferredDate;
  const currentTimeslot = request?.requestDetails?.preferredTimeSlot;
  const preferredLanguage = request?.requestDetails?.preferredLanguage;
  const postalCode = request?.requestDetails?.postalCode;

  const { data: availableDaysData } = useAuthQuery<
    {
      getEnergyConsultationMonthAvailability: EnergyConsultationMonthAvailabilityResponse;
    },
    QueryGetEnergyConsultationMonthAvailabilityArgs
  >(getAvailableDatesForConsultation, {
    variables: {
      input: {
        preferredLanguage,
        postalCode,
      },
    },
    skip: !preferredLanguage,
  });

  const form = useFormState(
    { newPreferredDate: undefined, newPreferredTimeSlot: '' },
    {
      validate,
      validationContext: {
        t,
      },
    },
  );

  useEffect(() => {
    void form.setValue('newPreferredDate', '');
    void form.setValue('newPreferredTimeSlot', '');
  }, [requestId]);

  const [rescheduleEnergyConsultation] = useMutation<
    {
      rescheduleEnergyConsultation: RescheduleEnergyConsultationResponse;
    },
    MutationRescheduleEnergyConsultationArgs
  >(rescheduleEnergyConsultationMutation);

  const setNotification = (
    message: string,
    severity: 'error' | 'success' = 'success',
  ) => {
    notificationContext?.setState?.({
      isOpen: true,
      severity,
      message,
    });

    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const rescheduleConsultationRequest = wrapWithLoader(
    async (values: RescheduleEnergyConsultationForm) => {
      try {
        const { data } = await rescheduleEnergyConsultation({
          variables: {
            payload: {
              newPreferredDate: form.values.newPreferredDate
                ? format(form.values.newPreferredDate, 'yyyy-MM-dd')
                : '',
              newPreferredTimeSlot: values.newPreferredTimeSlot,
              requestId,
              preferredLanguage,
            },
          },
          refetchQueries: ['getEnergyConsultationRequests'],
        });
        if (data?.rescheduleEnergyConsultation?.success) {
          setNotification(
            t('ENERGY_CONSULTATION_REQUEST_REDCHEDULED_SUCCESSFULLY'),
          );
          onClose();
        }
      } catch (e) {
        setNotification(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'), 'error');
      }
    },
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>
          {t('RESCHEDULE_CONSULTATION_REQUEST')}
        </Typography>

        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className={classes.content}>
        <Typography className={classes.fieldLabel}>
          {t('CURRENT_APPOINTMENT')}
        </Typography>
        <Typography className={classes.fieldValue}>
          {currentDate ? formatDate(new Date(currentDate), 'MMMM d, yyyy') : ''}{' '}
          at {currentTimeslot}
        </Typography>

        <Box>
          <ConsultationScheduler
            availableDays={
              availableDaysData?.getEnergyConsultationMonthAvailability?.days ||
              []
            }
            isModal={true}
            date={form.values.newPreferredDate || null}
            onDateChange={e => form.setValue('newPreferredDate', e)}
            time={form.values.newPreferredTimeSlot}
            onTimeChange={e => form.setValue('newPreferredTimeSlot', e)}
            preferredLanguage={preferredLanguage}
            postalCode={postalCode!}
          />
        </Box>

        <Box className={classes.footer}>
          <Button
            variant="outlined"
            onClick={onClose}
            className={classes.cancelButton}
          >
            {t('CANCEL')}
          </Button>

          <Button
            variant="contained"
            className={classes.submitButton}
            onClick={form.submit(rescheduleConsultationRequest)}
            disabled={
              !form.values.newPreferredDate || !form.values.newPreferredTimeSlot
            }
          >
            {t('SUBMIT')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleConsultationModal;
